import { useContext, useState } from "react";
import { Modal, Container, Row, Col, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { IDocumentTemplate } from "types";
import { useFormFields } from "hooks";
import { UserContext } from "context";
import { isEmpty } from "lodash";
import DocumentTemplateForm from "./DocumentTemplateForm";
import ConfirmModal from "components/Shared/ConfirmModal";

interface Props {
    documentTemplate: IDocumentTemplate;
    handleCreate: (data: Omit<IDocumentTemplate, "Id">) => void;
    handleUpdate: (data: IDocumentTemplate) => void;
    handleDelete: (id: string) => void;
    handleClose: () => void;
}

const DocumentTemplateModal: React.FC<Props> = ({
    documentTemplate,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleClose,
}) => {
    const { fields, handleFieldChange, setFields } = useFormFields<IDocumentTemplate>(documentTemplate);
    const [isInvalid, setIsInvalid] = useState<string[]>([]);
    const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
    const { user } = useContext(UserContext);
    const isValid =
        !isEmpty(fields.name) &&
        !isEmpty(fields.disciplineId) &&
        !isEmpty(fields.knowledgeId) &&
        !isEmpty(fields.documentKindId);
    const newTemplate = documentTemplate.id === "";
    const { t } = useTranslation();

    const handleSubmit = () => {
        const valid = validateFields();
        if (valid) {
            if (newTemplate) {
                handleCreate(fields);
            } else {
                handleUpdate(fields);
            }
            handleClose();
        }
    };

    const validateFields = (): boolean => {
        const invalid = [];
        const nameIsValid = () => fields.name !== "";
        const knowledgeIdIsValid = () => fields.knowledgeId !== "";
        const disciplineIdIsValid = () => fields.disciplineId !== "";
        const documentKindIdIsValid = () => fields.documentKindId !== "";
        const documentKindCategoryIdIsValid = () => fields.documentKindCategoryId !== "";
        if (!nameIsValid()) {
            invalid.push("NAME");
        }
        if (!knowledgeIdIsValid()) {
            invalid.push("KNOWLEDGEID");
        }
        if (!disciplineIdIsValid()) {
            invalid.push("DISCIPLINEID");
        }
        if (!documentKindIdIsValid()) {
            invalid.push("DOCUMENTKINDID");
        }
        if (!documentKindCategoryIdIsValid()) {
            invalid.push("DOCUMENTKINDCATEGORYID");
        }
        if (invalid.length > 0) {
            setIsInvalid(invalid);
            return false;
        }
        return true;
    };

    return (
        <Modal size="lg" show={true} onHide={handleClose} backdrop="static" centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {documentTemplate?.name === ""
                        ? `${t("pages.shared.templates.document.title-new")}`
                        : documentTemplate.name}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <DocumentTemplateForm
                    fields={fields}
                    handleFieldChange={handleFieldChange}
                    setFields={setFields}
                    setIsInvalid={setIsInvalid}
                    isInvalid={isInvalid}
                />
                {user?.role !== "admin" ? (
                    <Container>
                        <Row style={{ justifyContent: "flex-end" }}>
                            <Col style={{ textAlign: "end" }}>
                                <small className="text-muted">{t("form.only-admin-edit")}</small>
                            </Col>
                        </Row>
                    </Container>
                ) : null}
                {documentTemplate?.id === "" && !isValid ? (
                    <Container>
                        <Row style={{ justifyContent: "flex-end" }}>
                            <Col style={{ textAlign: "end" }}>
                                <small className="text-muted">{t("form.fillout")}</small>
                            </Col>
                        </Row>
                    </Container>
                ) : null}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    {t("buttons.cancel")}
                </Button>
                {!newTemplate && (
                    <Button
                        disabled={user?.role !== "admin"}
                        variant="danger"
                        onClick={() => {
                            setShowConfirmModal(true);
                        }}
                    >
                        {t("buttons.delete")}
                    </Button>
                )}
                <Button
                    disabled={isInvalid.length > 0 || user?.role !== "admin"}
                    variant="primary"
                    onClick={handleSubmit}
                >
                    {documentTemplate?.id === "" ? `${t("buttons.create")}` : `${t("buttons.save")}`}
                </Button>
            </Modal.Footer>
            <ConfirmModal
                show={showConfirmModal}
                handleAction={() => {
                    handleDelete(fields.id);
                    setShowConfirmModal(false);
                    handleClose();
                }}
                handleClose={() => {
                    setShowConfirmModal(false);
                    handleClose();
                }}
                description={t("pages.shared.templates.document.delete-title")}
                confirmText={t("buttons.confirm")}
            />
        </Modal>
    );
};

export default DocumentTemplateModal;
